import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { authAPI } from '../services/api'

const AuthContext = createContext({})

export const ROLE_OWNER = 'owner'
export const ROLE_STAFF = 'staff'

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Role gating here is UX only — the API re-checks the role on every request.
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  const storeUser = (userData) => {
    localStorage.setItem('admin_user', JSON.stringify(userData))
    setUser(userData)
  }

  const logout = async () => {
    try { await authAPI.logout() } catch {}
    sessionStorage.removeItem('platform_token')
    localStorage.removeItem('admin_user')
    setUser(null)
  }

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password)
      const { token, user: userData } = response.data
      // Token in sessionStorage for Bearer auth (cleared when tab closes)
      // httpOnly cookie is also set by the server for future cookie-based requests
      if (token) sessionStorage.setItem('platform_token', token)
      storeUser(userData)
      return { success: true, user: userData }
    } catch (error) {
      throw new Error(error.response?.data?.error?.message || error.response?.data?.error || error.message || 'Falha ao entrar')
    }
  }

  // Re-read role / must_change_password from the server (e.g. after an owner
  // changed this user's role, or after a password change).
  const refreshUser = useCallback(async () => {
    const res = await authAPI.getProfile()
    if (res.data?.user) storeUser(res.data.user)
    return res.data?.user
  }, [])

  const changePassword = async (currentPassword, newPassword) => {
    const res = await authAPI.changePassword(currentPassword, newPassword)
    if (res.data?.user) storeUser(res.data.user)
    return res.data
  }

  useEffect(() => {
    const initializeAuth = async () => {
      // If a session token exists from a previous login in this tab, restore the session
      const storedToken = sessionStorage.getItem('platform_token')
      const storedUser = localStorage.getItem('admin_user')
      if (storedToken && storedUser) {
        try {
          setUser(JSON.parse(storedUser))
        } catch {
          sessionStorage.removeItem('platform_token')
          localStorage.removeItem('admin_user')
        }
        // A 401 here is handled by the API interceptor (redirect to /login)
        try { await refreshUser() } catch {}
      }
      setLoading(false)
    }

    initializeAuth()
  }, [refreshUser])

  // Sessions from before roles existed have no role stored: only the owner could log in then.
  const role = user ? (user.role === ROLE_STAFF ? ROLE_STAFF : ROLE_OWNER) : null

  const value = {
    user,
    loading,
    login,
    logout,
    refreshUser,
    changePassword,
    isAuthenticated: !!user,
    isPlatformAdmin: true,
    role,
    isOwner: role === ROLE_OWNER,
    isStaff: role === ROLE_STAFF,
    mustChangePassword: !!user?.must_change_password,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
